import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import CloseIcon from "@mui/icons-material/Close";
import axios from "../../utils/axiosInstance";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function DeleteRecipient({
  open,
  onClose,
  groupId,
  onRecipientsUpdated,
  filteredUsers = [],
}) {
  const [currentUsers, setCurrentUsers] = useState([]);
  const [stagedForRemovalData, setStagedForRemoval] = useState([]); // Users staged for removal
  const [currentUsersSearchQuery, setCurrentUsersSearchQuery] = useState("");
  const [stagedSearchQuery, setStagedSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [removingUsers, setRemovingUsers] = useState(new Set());
  const [restoringUsers, setRestoringUsers] = useState(new Set());
  const [savingChanges, setSavingChanges] = useState(false);

  // Notification states
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Filter current users based on search query
  const filteredCurrentUsers = React.useMemo(() => {
    const remainingUsersData = currentUsers.filter(
      (user) =>
        !stagedForRemovalData.some((stagedUser) => stagedUser.id === user.id)
    );

    if (!currentUsersSearchQuery) return remainingUsersData;
    return remainingUsersData.filter(
      (data) =>
        (data.user.name || "")
          .toLowerCase()
          .includes(currentUsersSearchQuery.toLowerCase()) ||
        (data.user.email || "")
          .toLowerCase()
          .includes(currentUsersSearchQuery.toLowerCase()) ||
        (data.user.phone || "")
          .toLowerCase()
          .includes(currentUsersSearchQuery.toLowerCase())
    );
  }, [currentUsers, stagedForRemovalData, currentUsersSearchQuery]);

  // Filter staged users based on search query
  const filteredStagedUsers = React.useMemo(() => {
    if (!stagedSearchQuery) return stagedForRemovalData;
    return stagedForRemovalData.filter(
      (data) =>
        (data.user.name || "")
          .toLowerCase()
          .includes(stagedSearchQuery.toLowerCase()) ||
        (data.user.email || "")
          .toLowerCase()
          .includes(stagedSearchQuery.toLowerCase()) ||
        (data.user.phone || "")
          .toLowerCase()
          .includes(stagedSearchQuery.toLowerCase())
    );
  }, [stagedForRemovalData, stagedSearchQuery]);

  useEffect(() => {
    if (open && filteredUsers.length > 0) {
      setCurrentUsers(filteredUsers);
      setStagedForRemoval([]);
    }
    // Reset search queries when dialog opens
    setCurrentUsersSearchQuery("");
    setStagedSearchQuery("");
  }, [open, filteredUsers]);

  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // Stage user for removal (move from current to staged)
  const handleStageForRemoval = async (user) => {
    setRemovingUsers((prev) => new Set([user.id, ...prev]));
    try {
      // Move user from current to staged for removal
      setStagedForRemoval((prev) => [user, ...prev]);
      showNotification(`${user.name} staged for removal`, "success");
    } catch (error) {
      console.error(`Error staging user ${user.id} for removal:`, error);
      showNotification(`Error staging ${user.name} for removal`, "error");
    } finally {
      setRemovingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.id);
        return newSet;
      });
    }
  };

  // Restore user from staged removal (move back to current)
  const handleRestoreUser = async (user) => {
    setRestoringUsers((prev) => new Set([user.id, ...prev]));
    try {
      // Move user back from staged to current
      setStagedForRemoval((prev) => prev.filter((u) => u.id !== user.id));
      showNotification(`${user.name} restored to recipients`, "success");
    } catch (error) {
      console.error(`Error restoring user ${user.id}:`, error);
      showNotification(`Error restoring ${user.name}`, "error");
    } finally {
      setRestoringUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.id);
        return newSet;
      });
    }
  };

  // Save changes (actually remove users via API)
  const handleSaveChanges = async () => {
    if (stagedForRemovalData.length === 0) {
      showNotification("No users to remove", "warning");
      return;
    }

    
    setSavingChanges(true);
    try {
      const memberIds = stagedForRemovalData.map((member) => member.id);
      // const userIds = stagedForRemovalData.map(user => user.id);
      // const body = { userIds: userIds };
      const body = { UI_Description: `${getUIDescription()}\n Delete one or many members`, ids: memberIds };

      await axios.delete(
        // `${process.env.REACT_APP_API_GATEAWAY}/recipients/notificationGroup/${groupId}`,
        `${process.env.REACT_APP_API_GATEAWAY}/members`,
        {
          data: body,
        }
      );

      showNotification(
        `${stagedForRemovalData.length} user(s) removed successfully`,
        "success"
      );

      // Clear staged users after successful removal
      setStagedForRemoval([]);

      // Refresh parent component data
      if (onRecipientsUpdated) {
        onRecipientsUpdated();
      }
    } catch (error) {
      console.error(`Error removing users:`, error);
      showNotification(`Error removing users`, "error");
    } finally {
      setSavingChanges(false);
    }
  };

  // Handle dialog close with unsaved changes
  const handleClose = () => {
    if (stagedForRemovalData.length > 0) {
      // Clear staged removals when closing without saving
      setStagedForRemoval([]);
    }
    onClose();
  };

  const handleSaveAndClose = async () => {
    await handleSaveChanges();
    if (onRecipientsUpdated) {
      onRecipientsUpdated();
    }
    onClose();
  };

  // User list item component
  const UserListItem = ({
    user,
    showRemoveButton,
    showRestoreButton,
    isStaged = false,
  }) => {
    return (
      <ListItem
        sx={{
          border: 1,
          borderColor: isStaged ? "error.main" : "divider",
          borderRadius: 1,
          mb: 1,
          backgroundColor: isStaged ? "error.light" : "background.paper",
          opacity: isStaged ? 0.8 : 1,
          "&:hover": {
            backgroundColor: isStaged ? "error.light" : "action.hover",
          },
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{
                  textDecoration: isStaged ? "line-through" : "none",
                  color: isStaged ? "error.dark" : "inherit",
                }}
              >
                {user.user.name}
              </Typography>
              {isStaged && (
                <Typography
                  variant="caption"
                  color="error.dark"
                  fontWeight="bold"
                >
                  (TO REMOVE)
                </Typography>
              )}
            </Box>
          }
          secondary={
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textDecoration: isStaged ? "line-through" : "none",
                }}
              >
                {user.user.email}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textDecoration: isStaged ? "line-through" : "none",
                }}
              >
                {user.user.phone}
              </Typography>
            </Box>
          }
        />
        <ListItemSecondaryAction>
          {showRemoveButton && (
            <IconButton
              edge="end"
              onClick={() => handleStageForRemoval(user)}
              disabled={removingUsers.has(user.id)}
              sx={{
                backgroundColor: "error.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "error.dark",
                },
                "&:disabled": {
                  backgroundColor: "action.disabled",
                },
              }}
            >
              {removingUsers.has(user.id) ? (
                <CircularProgress size={20} color="#60a5fa" />
              ) : (
                <DeleteIcon />
              )}
            </IconButton>
          )}
          {showRestoreButton && (
            <IconButton
              edge="end"
              onClick={() => handleRestoreUser(user)}
              disabled={restoringUsers.has(user.id)}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
                "&:disabled": {
                  backgroundColor: "action.disabled",
                },
              }}
            >
              {restoringUsers.has(user.id) ? (
                <CircularProgress size={20} color="#60a5fa" />
              ) : (
                <RestoreIcon />
              )}
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "80vh",
            maxHeight: "800px",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Remove Recipients{" "}
              {stagedForRemovalData.length > 0 &&
                `(${stagedForRemovalData.length} to remove)`}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px",
              }}
            >
              <CircularProgress color="#60a5fa" size={60} />
            </Box>
          ) : (
            <>
              {/* Warning Message */}
              <Typography
                variant="body2"
                color="error"
                sx={{ mb: 2, fontStyle: "italic" }}
              >
                Recipients to be removed will no longer receive notifications
                from this group
              </Typography>

              {/* Staged changes info */}
              {stagedForRemovalData.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {stagedForRemovalData.length} user(s) staged for removal.
                  Click "Remove Recipients" to confirm.
                </Alert>
              )}

              <Box sx={{ display: "flex", height: "500px", gap: 2 }}>
                {/* Left Panel - Current Recipients */}
                <Box sx={{ width: "50%" }}>
                  <Paper
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: "grey.800",
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "grey.700",
                        color: "white",
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        CURRENT RECIPIENTS
                      </Typography>
                    </Box>

                    {/* Search */}
                    <Box sx={{ p: 2 }}>
                      <TextField
                        placeholder="SEARCH"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={currentUsersSearchQuery}
                        onChange={(e) =>
                          setCurrentUsersSearchQuery(e.target.value)
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "black",
                          },
                        }}
                      />
                    </Box>

                    {/* Users List */}
                    <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 2 }}>
                      <List sx={{ py: 0 }}>
                        {filteredCurrentUsers.map((user) => (
                          <UserListItem
                            key={user.id}
                            user={user}
                            showRemoveButton={true}
                            showRestoreButton={false}
                            isStaged={false}
                          />
                        ))}
                        {filteredCurrentUsers.length === 0 && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                            sx={{ py: 4, color: "grey.400" }}
                          >
                            {stagedForRemovalData.length > 0
                              ? "All users have been staged for removal"
                              : "No current recipients found"}
                          </Typography>
                        )}
                      </List>
                    </Box>
                  </Paper>
                </Box>

                {/* Right Panel - Recipients to be Removed */}
                <Box sx={{ width: "50%" }}>
                  <Paper
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: "grey.800",
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "error.dark",
                        color: "white",
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        RECIPIENTS TO BE REMOVED
                      </Typography>
                    </Box>

                    {/* Search */}
                    <Box sx={{ p: 2 }}>
                      <TextField
                        placeholder="SEARCH"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={stagedSearchQuery}
                        onChange={(e) => setStagedSearchQuery(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "black",
                          },
                        }}
                      />
                    </Box>

                    {/* Staged Users List */}
                    <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 2 }}>
                      <List sx={{ py: 0 }}>
                        {filteredStagedUsers.map((user) => (
                          <UserListItem
                            key={user.id}
                            user={user}
                            showRemoveButton={false}
                            showRestoreButton={true}
                            isStaged={true}
                          />
                        ))}
                        {filteredStagedUsers.length === 0 && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                            sx={{ py: 4, color: "grey.400" }}
                          >
                            No recipients staged for removal
                          </Typography>
                        )}
                      </List>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderRadius: 0, // ✅ remove rounding
              bgcolor: "grey.400",
              color: "white",
              "&:hover": {
                bgcolor: "grey.500",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAndClose}
            disabled={stagedForRemovalData.length === 0 || savingChanges}
            sx={{
              bgcolor: "error.main",
              "&:hover": {
                bgcolor: "error.dark",
              },
              "&:disabled": {
                bgcolor: "grey.300",
              },
            }}
          >
            {savingChanges ? (
              <>
                <CircularProgress size={20} color="#60a5fa" sx={{ mr: 1 }} />
                Removing...
              </>
            ) : (
              `Remove Recipients ${
                stagedForRemovalData.length > 0
                  ? `(${stagedForRemovalData.length})`
                  : ""
              }`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}
